<template>
  <div class="event-details apt-details" v-if="pageLoaded">
    <!-- Waiting for MY answer → accept / reject banner -->
    <div class="apt-details__answer" v-if="eventSubmission !== null">
      <span class="apt-details__answer-text">
        <feather-icon icon="BellIcon" size="14" class="mr-25"/>{{ $t('calendar.pendingInvites') }}
      </span>
      <span class="apt-details__answer-actions">
        <b-button size="sm" v-bind:variant="eventSubmission === CalendarStatusEnum.APPROVED ? 'success' : 'outline-success'" @click="responseEvent(CalendarStatusEnum.APPROVED)">{{$t('calendar.action.accept')}}</b-button>
        <b-button size="sm" v-bind:variant="eventSubmission === CalendarStatusEnum.REJECTED ? 'danger' : `outline-danger`" @click="responseEvent(CalendarStatusEnum.REJECTED)">{{$t('calendar.action.reject')}}</b-button>
      </span>
    </div>

    <!-- Facts grid -->
    <div class="apt-details__grid">
      <div class="apt-detail">
        <span class="apt-detail__icon"><i class="far fa-clock"></i></span>
        <span class="apt-detail__body">
          <span class="apt-detail__label">{{ $t('calendar.start') }}</span>
          <span class="apt-detail__value">{{ event.start | momentDateTime }}</span>
        </span>
      </div>
      <div class="apt-detail">
        <span class="apt-detail__icon"><i class="fas fa-clock"></i></span>
        <span class="apt-detail__body">
          <span class="apt-detail__label">{{ $t('calendar.end') }}</span>
          <span class="apt-detail__value">{{ event.end | momentDateTime }}</span>
        </span>
      </div>
      <div class="apt-detail">
        <span class="apt-detail__icon"><i class="far fa-user"></i></span>
        <span class="apt-detail__body">
          <span class="apt-detail__label">{{ $t('calendar.host') }}</span>
          <span class="apt-detail__value">{{ getUserFullName(event.userId) }}</span>
        </span>
      </div>
      <div class="apt-detail">
        <span class="apt-detail__icon"><i class="fa-regular fa-building"></i></span>
        <span class="apt-detail__body">
          <span class="apt-detail__label">{{ $t('columns.org') }}</span>
          <span class="apt-detail__value">{{ getOrganizationNameById(event.orgId) || '—' }}</span>
        </span>
      </div>
      <div class="apt-detail" v-if="event.location != null && event.location.trim().length > 0">
        <span class="apt-detail__icon"><i class="fas fa-map-marker-alt"></i></span>
        <span class="apt-detail__body">
          <span class="apt-detail__label">{{ $t('calendar.location') }}</span>
          <span class="apt-detail__value">{{ event.location }}</span>
        </span>
      </div>
      <div class="apt-detail" v-if="event.url != null && event.url.trim().length > 0">
        <span class="apt-detail__icon"><i class="fas fa-at"></i></span>
        <span class="apt-detail__body">
          <span class="apt-detail__label">{{ $t('calendar.url') }}</span>
          <b-link :href="event.url" target="_blank" class="apt-detail__value text-lowercase">{{ event.url }}</b-link>
        </span>
      </div>
    </div>

    <!-- Participants -->
    <div class="apt-details__section" v-if="event.users != null && event.users.length > 0">
      <div class="apt-details__section-title">
        <feather-icon icon="UsersIcon" size="14" class="mr-25"/>{{ $t('calendar.representatives') }}
      </div>
      <b-avatar-group>
        <user-avatar badge v-for="user in event.users" v-bind:key="user.userId" v-bind:badge-variant="badgeVariant(user)"
                     :user-id="user.userId" :size="30"/>
      </b-avatar-group>
    </div>

    <!-- Clients -->
    <div class="apt-details__section" v-if="hasClients">
      <div class="apt-details__section-title">
        <i class="fas fa-users mr-25"></i>{{ $t('calendar.clients') }}
      </div>
      <b-avatar-group v-if="loading.clients !== true">
        <b-avatar button class="pull-up" size="sm" variant="primary"
                  v-for="client in clients" v-bind:key="client.id"
                  :text="client.acronym"
                  v-b-tooltip :title="client.name"
                  @click="onClientSelect(client.id)"
        ></b-avatar>
      </b-avatar-group>
      <b-avatar-group v-else>
        <b-skeleton v-for="client in event.clientsIds" v-bind:key="client" type="avatar" @click="onClientSelect(client)"></b-skeleton>
      </b-avatar-group>
    </div>

    <!-- Description -->
    <div class="apt-details__section" v-if="event.body != null && event.body.trim().length > 0">
      <div class="apt-details__section-title">
        <feather-icon icon="AlignJustifyIcon" size="14" class="mr-25"/>{{ $t('calendar.description') }}
      </div>
      <p class="apt-details__notes">{{ event.body }}</p>
    </div>
  </div>
  <div class="event-details" v-else-if="error !== ''">
    <b-alert show variant="danger"><i class="fas fa-exclamation-triangle mr-3"></i> <strong>{{ error }}</strong>
    </b-alert>
  </div>
  <div class="event-details" v-else>
    Loading
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import UserAvatar from '@/components/widget/UserAvatar'
import mixinBase from '@/components/mixin/mixinBase'
import { CalendarStatusEnum } from '@/model/calendar/CalendarStatusEnum'
import { isEmptyList } from '@/model/Utils'

export default {
  name: 'calendarEventBasic',
  mixins: [mixinBase],
  components: { UserAvatar },
  data: () => ({
    CalendarStatusEnum,
    loading: { event:true, clients: true },
    error: '',
    clients: [],
    //Listeners
    listeners: { load: () => {} }
  }),
  created() {
    this.reloadClients();
  },
  props: {
    event: Object,
    external: {
      default: false,
      type: Boolean
    }
  },
  computed: {
    ...mapState({
      data: function (store) {
        return store.data
      }
    }),
    ...mapGetters('user',['myId']),
    ...mapGetters('data', ['getUserFullName', 'getUserAcronyms', 'getOrganizationNameById']),
    isExternal() {
      return this.external === true
    },
    hasClients() {
      return this.event.clientsIds != null && this.event.clientsIds.length > 0
    },
    hasOnlyOneClient() {
      return this.hasClients && this.event.clientsIds.length === 1
    },
    uniqueClient() {
      if (!this.hasOnlyOneClient) {
        return {}
      }
      return this.clients[0]
    },
    pageLoaded() {
      return this.event != null && typeof this.event === 'object'
    },
    eventColor() {
      if (this.event == null || this.event.color == null) {
        return '#1867c0'
      }
      return this.event.color
    },
    eventTime() {
      if (this.event == null || this.event.date == null) {
        return 'Date is null'
      }
      return this.$moment(this.event.date).format('YYYY-MM-DD HH:mm')
    },
    eventSubmission(){
      if(this.event == null || isEmptyList(this.event.users) || this.event.userId === this.myId){
        return null;
      }
      if(this.event.status === CalendarStatusEnum.CANCELLED){
        return null;
      }
      let me = this.event.users.find(u => u.userId === this.myId);
      if(me == null){
        return null;
      }
      return me.status || CalendarStatusEnum.PENDING;
    }
  },
  methods: {
    ...mapActions('calendar', ['getCalendarById', 'updateUserStatus']),
    ...mapActions('clients', ['getAllClients']),
    load() {
      let $this = this
      $this.loading.event = true
      $this.loading.clients = true
      console.log('load calendar: ' + event.title)
      this.get({ id: $this.event.id })
          .then((event) => {
            if (event == null) {
              return $this.error = 'Load event failed'
            }
            $this.reloadClients();
            $this.loading.event = false
          }, (error) => {
            $this.error = error
          })
    },
    reloadClients(){
      let $this = this;
      $this.loading.clients = true
      if (this.event.clientsIds == null || this.event.clientsIds.length <= 0) {
        this.clients = [];
        $this.loading.clients = false;
        return
      }
      this.getAllClients({ filter: { ids: this.event.clientsIds } })
          .then(
              (clientData) => $this.clients = clientData.data.map(client => {
                let fn = client.firstName != null ? client.firstName.trim() : ''
                let ln = client.lastName != null ? client.lastName.trim() : ''
                let acronym = (fn !== '' ? fn.charAt(0)
                    .toUpperCase() : '') + (ln !== '' ? ln.charAt(0)
                    .toUpperCase() : '')
                return { id: client.id, name: fn + ' ' + ln, acronym }
              })
          ).finally(() => $this.loading.clients = false)
    },
    onClientSelect(id) {
      console.log('CalendarEvent -> onClientSelect', id)
      this.showClient(id)
    },
    badgeVariant(userData){
      if(userData == null || userData.status == null || userData.status === CalendarStatusEnum.PENDING){
        return 'dark';
      }
      if(userData.status === CalendarStatusEnum.APPROVED){
        return 'success';
      }
      if(userData.status === CalendarStatusEnum.REJECTED){
        return 'danger';
      }
      return 'dark';
    },
    responseEvent(status){
      let $this = this;
      this.loading.event = true;
      this.updateUserStatus({eventId: this.event.id, userId:this.myId, status: status})
          .then(calendarEvent => $this.$emit('updated', calendarEvent)).finally(() => $this.loading.event = false)
    }
  }
}
</script>

<style scoped>
p {
  margin: 0;
}
</style>

<style lang="scss">
/* Info-grid layout for the event details (modal body). Unscoped — keyed to
   .apt-details — because the modal content is portaled to <body>. */
.apt-details {
  &__answer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: .5rem;
    padding: .55rem .75rem;
    margin-bottom: 1rem;
    border-radius: .65rem;
    background: rgba(255, 159, 67, .12);
    border: 1px solid rgba(255, 159, 67, .35);
  }
  &__answer-text {
    display: inline-flex;
    align-items: center;
    font-size: .8rem;
    font-weight: 700;
    color: #b06a1f;
  }
  &__answer-actions {
    display: inline-flex;
    gap: .4rem;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: .6rem;
  }

  &__section {
    margin-top: 1.1rem;
  }
  &__section-title {
    display: flex;
    align-items: center;
    font-size: .74rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .4px;
    color: #a6a4b0;
    margin-bottom: .5rem;
  }
  &__notes {
    font-size: .88rem;
    color: #5e5873;
    white-space: pre-line;
    margin: 0;
  }
}

.apt-detail {
  display: flex;
  align-items: center;
  gap: .6rem;
  padding: .5rem .6rem;
  border-radius: .6rem;
  background: #f8f8fb;

  &__icon {
    width: 32px;
    height: 32px;
    border-radius: .55rem;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #7367f0;
    background: rgba(115, 103, 240, .12);
    font-size: .8rem;
  }
  &__body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    line-height: 1.25;
  }
  &__label {
    font-size: .68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #b9b9c3;
  }
  &__value {
    font-size: .85rem;
    font-weight: 600;
    color: #5e5873;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.dark-layout {
  .apt-detail {
    background: #343d55;
    &__value { color: #d0d2d6; }
  }
  .apt-detail__value { color: #d0d2d6; }
  .apt-details__notes { color: #d0d2d6; }
}
</style>
<style>
.event-details .ps .ps__rail-x,
.event-details .ps .ps__rail-y {
  opacity: 1;
}

.event-details .ps__rail-y > .ps__thumb-y {
  width: 11px;
}

.event-details .ps .ps__rail-y {
  background-color: #eee;
  opacity: 0.9;
}
</style>
